import type Phaser from "phaser";
import { meanOpaqueColour, type AssetManifest, type FrameRect } from "daedalus-core";
import { pageTextureKey } from "./atlas.ts";
import type { PictureAtlas, PictureSurface } from "./map-picture.ts";

/**
 * The browser and Phaser sides of the map picture painter: a canvas surface that hears its context being lost, the
 * atlas pages the scene already holds as the painter's source images, and a canvas texture made from a picture. A
 * picture's canvas texture is re-uploaded by Phaser from its canvas when the WebGL context returns, so a lost
 * WebGL context costs the pictures no recompose (decision 43); only a lost 2D context of the canvas itself does.
 */

/**
 * A source image of an atlas page, as the browser draws it.
 */
export type PageImage = HTMLImageElement | HTMLCanvasElement;

/**
 * A surface backed by a canvas element, which a texture is made from.
 */
export interface DomSurface extends PictureSurface<PageImage> {
  readonly canvas: HTMLCanvasElement;
}

/**
 * Makes a canvas surface of a size.
 *
 * @param width Width in pixels.
 * @param height Height in pixels.
 * @param readFrequently True when the surface's pixels will be read back often, so the browser keeps it where a read is cheap.
 * @returns The surface, or null when the browser gives no 2D context for it.
 */
export function createDomSurface(width: number, height: number, readFrequently = false): DomSurface | null {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = readFrequently ? canvas.getContext("2d", { willReadFrequently: true }) : canvas.getContext("2d");
  if (context === null) {
    return null;
  }
  return {
    canvas,
    width,
    height,
    context,
    onLost: (listener) => canvas.addEventListener("contextlost", listener),
    onRestored: (listener) => canvas.addEventListener("contextrestored", listener),
    dispose: () => {
      canvas.width = 0;
      canvas.height = 0;
    },
  };
}

/**
 * The atlas pages a scene already holds, read as the painter's source images. A page whose texture is not
 * registered or whose source is not an image or canvas reads as not loaded. It also gives a frame's mean opaque
 * colour, read once from a scratch canvas and kept for the atlas's life (null for a frame with no opaque pixel or
 * one the browser would not read back).
 *
 * @param scene The scene whose texture manager holds the pages.
 * @param manifest The asset manifest naming the pages.
 * @returns The atlas.
 */
export function scenePageAtlas(scene: Phaser.Scene, manifest: AssetManifest): PictureAtlas<PageImage> {
  const means = new Map<string, { readonly r: number; readonly g: number; readonly b: number } | null>();
  let scratch: HTMLCanvasElement | null = null;
  /**
   * Reads a page's source image from the texture manager.
   *
   * @param page The page index.
   * @returns The page image, or null when the page or its texture is missing.
   */
  const imageOf = (page: number): PageImage | null => {
    const entry = manifest.pages[page];
    if (entry === undefined) {
      return null;
    }
    const key = pageTextureKey(manifest, page);
    if (!scene.textures.exists(key)) {
      return null;
    }
    const source = scene.textures.get(key).getSourceImage();
    return source instanceof HTMLImageElement || source instanceof HTMLCanvasElement ? source : null;
  };
  return {
    image: imageOf,
    /**
     * Gives the mean opaque colour of a frame, read once and kept.
     *
     * @param frame The frame id.
     * @param page The page index.
     * @param rect The frame's rectangle on its page.
     * @returns The mean colour, or null when the frame has no opaque pixel or cannot be read.
     */
    meanColour: (frame: string, page: number, rect: FrameRect) => {
      const kept = means.get(frame);
      if (kept !== undefined) {
        return kept;
      }
      const image = imageOf(page);
      if (image === null) {
        return null;
      }
      scratch ??= document.createElement("canvas");
      scratch.width = rect.w;
      scratch.height = rect.h;
      const context = scratch.getContext("2d", { willReadFrequently: true });
      let mean: { readonly r: number; readonly g: number; readonly b: number } | null = null;
      if (context !== null) {
        context.clearRect(0, 0, rect.w, rect.h);
        context.drawImage(image, rect.x, rect.y, rect.w, rect.h, 0, 0, rect.w, rect.h);
        mean = meanOpaqueColour(context.getImageData(0, 0, rect.w, rect.h).data);
      }
      means.set(frame, mean);
      return mean;
    },
  };
}

/**
 * Makes a scene texture from a surface's canvas, or refreshes it when it already exists, so the picture's pixels
 * reach the GPU. The texture is the one the overview's image shows.
 *
 * @param scene The scene.
 * @param key The texture key.
 * @param surface The surface whose canvas is shown.
 * @returns The texture.
 * @throws Error when the texture manager refuses the canvas.
 */
export function pictureTexture(scene: Phaser.Scene, key: string, surface: DomSurface): Phaser.Textures.CanvasTexture {
  const existing = scene.textures.exists(key) ? scene.textures.get(key) : null;
  if (existing !== null && existing.getSourceImage() === surface.canvas) {
    const texture = existing as Phaser.Textures.CanvasTexture;
    texture.refresh();
    return texture;
  }
  if (existing !== null) {
    scene.textures.remove(key);
  }
  const made = scene.textures.addCanvas(key, surface.canvas);
  if (made === null) {
    throw new Error(`could not make picture texture ${key}`);
  }
  return made;
}
